/** Привязка бота к каналам и ролям сервера. */

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { CHANNEL_LABELS, ROLE_LABELS, type WiringSettings } from '@ak/shared';

import { Button, Field, Select, TextInput } from '../../components/ui.tsx';
import { Section, type GuildStructure, type SettingsResponse } from './shared.tsx';

export interface WiringTabProps {
  data: SettingsResponse;
  wiringDraft: WiringSettings;
  setWiringDraft: Dispatch<SetStateAction<WiringSettings | null>>;
  structure: GuildStructure | null;
  onSave: () => void;
  busy: boolean;
}

export function WiringTab({ data, wiringDraft, setWiringDraft, structure, onSave, busy }: WiringTabProps): ReactNode {
  const channelOptions = structure?.available === true ? structure.channels.filter((c) => c.type !== 'voice') : [];
  const channelGroups = groupByCategory(channelOptions);
  const roleOptions = structure?.available === true ? structure.roles : [];

  const setChannel = (key: keyof WiringSettings['channels'], value: string): void => {
    setWiringDraft((current) =>
      current === null ? current : { ...current, channels: { ...current.channels, [key]: value || null } },
    );
  };

  const setRole = (key: keyof WiringSettings['roles'], value: string): void => {
    setWiringDraft((current) =>
      current === null ? current : { ...current, roles: { ...current.roles, [key]: value || null } },
    );
  };

  return (
    <Section
      title="Каналы и роли"
      description="Пустое поле означает «взять значение из .env». Заполненное перекрывает его."
    >
      {structure?.available === false && (
        <div className="rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--warning-soft)', color: 'var(--warning)' }}>
          Список каналов и ролей недоступен: {structure.reason ?? 'бот не подключён'}. Идентификаторы можно вписать
          вручную.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {(Object.keys(CHANNEL_LABELS) as (keyof WiringSettings['channels'])[]).map((key) => (
          <Field
            key={key}
            label={CHANNEL_LABELS[key]}
            hint={`Сейчас: ${data.wiring.effective.channels[key] ?? 'не задан'}`}
          >
            {channelGroups.length > 0 ? (
              <Select
                value={wiringDraft.channels[key] ?? ''}
                onChange={(event) => {
                  setChannel(key, event.target.value);
                }}
              >
                <option value="">— из .env —</option>
                {channelGroups.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        # {channel.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            ) : (
              <TextInput
                value={wiringDraft.channels[key] ?? ''}
                placeholder="из .env"
                onChange={(event) => {
                  setChannel(key, event.target.value);
                }}
              />
            )}
          </Field>
        ))}
      </div>

      <div className="grid gap-4 pt-2 lg:grid-cols-3">
        {(Object.keys(ROLE_LABELS) as (keyof WiringSettings['roles'])[]).map((key) => (
          <Field key={key} label={ROLE_LABELS[key]}>
            {roleOptions.length > 0 ? (
              <Select
                value={wiringDraft.roles[key] ?? ''}
                onChange={(event) => {
                  setRole(key, event.target.value);
                }}
              >
                <option value="">— из .env —</option>
                {roleOptions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            ) : (
              <TextInput
                value={wiringDraft.roles[key] ?? ''}
                placeholder="из .env"
                onChange={(event) => {
                  setRole(key, event.target.value);
                }}
              />
            )}
          </Field>
        ))}
      </div>

      {/*
        У привязки своя кнопка: она уходит отдельным запросом и сохраняется
        независимо от поведенческих настроек — ошибка в одном не должна
        отменять правку другого.
      */}
      <div className="flex justify-end pt-2">
        <Button variant="primary" onClick={onSave} busy={busy}>
          Сохранить привязку
        </Button>
      </div>
    </Section>
  );
}

type ChannelOption = GuildStructure['channels'][number];

/** Каналы, сгруппированные по категориям: без категории идут первыми. */
function groupByCategory(channels: readonly ChannelOption[]): { label: string; channels: ChannelOption[] }[] {
  if (channels.length === 0) {
    return [];
  }

  const groups: { label: string; channels: ChannelOption[] }[] = [];
  const noCategory = channels.filter((channel) => channel.parentName === null);
  if (noCategory.length > 0) {
    groups.push({ label: 'Без категории', channels: noCategory });
  }

  const byParent = new Map<string, ChannelOption[]>();
  for (const channel of channels) {
    if (channel.parentName !== null) {
      const existing = byParent.get(channel.parentName);
      if (existing) existing.push(channel);
      else byParent.set(channel.parentName, [channel]);
    }
  }

  for (const [label, list] of byParent) {
    groups.push({ label, channels: list });
  }

  return groups;
}
